import React, { useEffect, useCallback } from 'react';
import './proxy-stats-panel.css';

/**
 * ProxyStatsPanel — правая панель мини-дашборда со статистикой.
 * Figma desktop: Statistic (604:82053) — 280px inline sidebar.
 * Figma mobile:  Overlay (604:84239) — bottom-sheet с backdrop.
 *
 * Props:
 *   statusData — объект StatsResponse
 *   onClose    — закрыть панель (для мобильного крестика и backdrop)
 *   loading    — состояние загрузки статистики
 */

/* ---- SVG close icon (fi-rr-cross-small) ---- */
const CloseIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41L17.59 5Z"
      fill="#FFFFFF"
    />
  </svg>
);

/* ---- Row helper ---- */
const StatsRow = ({ label, value, variant = 'default' }) => (
  <div className="pxs__row">
    <span className={`pxs__label pxs__label--${variant === 'title' ? 'title' : 'sub'}`}>
      {label}
    </span>
    <span className={`pxs__value pxs__value--${variant}`}>
      {value}
    </span>
  </div>
);

const SkeletonBlock = ({ rows = 3 }) => (
  <div className="pxs__block pxs__block--skeleton">
    <div className="pxs__row">
      <span className="pxs__skeleton-bar" style={{ width: '60%' }} />
      <span className="pxs__skeleton-bar pxs__skeleton-bar--short" />
    </div>
    {Array.from({ length: rows }, (_, i) => (
      <div className="pxs__row" key={i}>
        <span className="pxs__skeleton-bar" style={{ width: `${50 + (i % 3) * 15}%` }} />
        <span className="pxs__skeleton-bar pxs__skeleton-bar--short" />
      </div>
    ))}
  </div>
);

const ProxyStatsPanel = ({ statusData, onClose, loading = false }) => {
  // ---- Close on Escape ----
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape' && onClose) onClose();
  }, [onClose]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  if (!statusData && !loading) return null;

  const {
    total_proxies = 0,
    available_proxies = 0,
    unavailable_proxies = 0,
    total_modems = 0,
    available_modems = 0,
    unavailable_modems = 0,
    unknown_modems = 0,
    for_sale_operators = null,
    for_sale_operators_by_city = null,
  } = statusData || {};

  const forSaleOperatorEntries = for_sale_operators && typeof for_sale_operators === 'object'
    ? Object.entries(for_sale_operators).sort((a, b) => b[1] - a[1] || String(a[0]).localeCompare(String(b[0]), 'ru'))
    : [];

  const forSaleCityOrder = ['МСК', 'СПБ'];
  const forSaleOperatorsByCityEntries = for_sale_operators_by_city && typeof for_sale_operators_by_city === 'object'
    ? Object.entries(for_sale_operators_by_city).sort(([cityA], [cityB]) => {
        const indexA = forSaleCityOrder.indexOf(cityA);
        const indexB = forSaleCityOrder.indexOf(cityB);
        if (indexA !== -1 || indexB !== -1) {
          return (indexA === -1 ? forSaleCityOrder.length : indexA)
            - (indexB === -1 ? forSaleCityOrder.length : indexB);
        }
        return String(cityA).localeCompare(String(cityB), 'ru');
      })
    : [];

  const forSaleTotalCount = forSaleOperatorEntries.reduce((sum, [, count]) => sum + Number(count || 0), 0);

  return (
    <>
      {/* Backdrop — visible only on mobile via CSS */}
      <div
        className="pxs-backdrop"
        onClick={onClose}
        aria-hidden="true"
      />

      <div className="pxs">
        {/* Title bar — visible only on mobile via CSS
            Figma Title (604:84241): "ДАШБОРД" + close icon */}
        <div className="pxs__title-bar">
          <h2 className="pxs__title-text">Дашборд</h2>
          <button
            type="button"
            className="pxs__title-close"
            onClick={onClose}
            aria-label="Закрыть дашборд"
            tabIndex={0}
          >
            <CloseIcon />
          </button>
        </div>

        {/* Content — scrollable on mobile */}
        <div className="pxs__body">
          {/* Block 1 — Всего прокси */}
          <div className="pxs__block">
            <StatsRow label="Всего прокси" value={total_proxies} variant="title" />
            <StatsRow label="Доступно" value={available_proxies} variant="green" />
            <StatsRow label="Недоступно" value={unavailable_proxies} variant="red" />
          </div>

          {/* Block 2 — Модемы */}
          <div className="pxs__block">
            <StatsRow label="Модемы" value={total_modems} variant="title" />
            <StatsRow label="Доступно" value={available_modems} variant="green" />
            <StatsRow label="Недоступно" value={unavailable_modems} variant="red" />
            <StatsRow label="Неизвестно" value={unknown_modems} variant="default" />
          </div>

          {loading ? (
            <SkeletonBlock rows={3} />
          ) : forSaleOperatorEntries.length > 0 ? (
            <div className="pxs__block">
              <StatsRow label="Операторы" value={forSaleTotalCount} variant="title" />
              {forSaleOperatorEntries.map(([operatorName, count]) => (
                <StatsRow key={operatorName} label={operatorName} value={count} variant="default" />
              ))}
            </div>
          ) : (
            <div className="pxs__block">
              <StatsRow label="Операторы" value={0} variant="title" />
              <StatsRow label="Нет данных" value={0} variant="default" />
            </div>
          )}

          {loading ? (
            <SkeletonBlock rows={4} />
          ) : forSaleOperatorsByCityEntries.length > 0 ? (
            <div className="pxs__block">
              <StatsRow label="В продаже" value={forSaleTotalCount} variant="title" />
              {forSaleOperatorsByCityEntries.map(([cityName, cityOperators]) => {
                const operatorsMap = cityOperators && typeof cityOperators === 'object' ? cityOperators : {};
                const cityOperatorEntries = Object.entries(operatorsMap)
                  .sort((a, b) => b[1] - a[1] || String(a[0]).localeCompare(String(b[0]), 'ru'));
                const cityTotal = cityOperatorEntries.reduce((sum, [, count]) => sum + Number(count || 0), 0);

                return (
                  <React.Fragment key={cityName}>
                    <StatsRow label={cityName} value={cityTotal} variant="title" />
                    {cityOperatorEntries.map(([name, count]) => (
                      <StatsRow
                        key={`${cityName}-${name}`}
                        label={name}
                        value={count}
                        variant="default"
                      />
                    ))}
                  </React.Fragment>
                );
              })}
            </div>
          ) : (
            <div className="pxs__block">
              <StatsRow label="В продаже" value={0} variant="title" />
              <StatsRow label="Нет данных" value={0} variant="default" />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ProxyStatsPanel;
